// Zod DTOs for the `/v1/packages` + `/v1/students/:id/packages` +
// `/v1/student-packages/:id` authoring/assignment surface
// (`docs/ADR-packages-entitlements.md` §6, Slice C, §9) — single source of
// truth consumed by `apps/api`'s route validators. `TEACHER_OR_ADMIN`
// (authoring is not restricted commerce, ADR §6.1). Mirrors `./level.ts` /
// `./roster.ts`'s header convention for this shape.
import { z } from 'zod';
import { PackageGrantType, PackageStatus } from '../enums';
import { codeSchema, descriptionSchema, idSchema, titleSchema } from './primitives';
import { currencyCodeSchema } from './billing';

const packageGrantTypeSchema = z.enum(
  Object.values(PackageGrantType) as [PackageGrantType, ...PackageGrantType[]],
);
const packageStatusSchema = z.enum(
  Object.values(PackageStatus) as [PackageStatus, ...PackageStatus[]],
);

/** One `PackagePrice` row on the wire (ADR §1.3) — integer minor units,
 * never a float; `currency` reuses the same ISO-4217 shape `./billing.ts`'s
 * `Subscription`-adjacent DTOs already validate against, so a package price
 * and a subscription amount can never drift on what a "valid currency
 * string" is. */
export const packagePriceSchema = z.object({
  currency: currencyCodeSchema,
  amountMinor: z.number().int().nonnegative('amountMinor must be >= 0'),
});
export type PackagePriceInput = z.infer<typeof packagePriceSchema>;

/** At most one row per currency — mirrors `PackagePrice`'s
 * `@@unique([packageId, currency])`; caught here too (app-level, on top of
 * the DB constraint) so a malformed body 400s before it ever reaches D1. */
function assertNoDuplicateCurrency(prices: PackagePriceInput[], ctx: z.RefinementCtx) {
  const seen = new Set<string>();
  prices.forEach((price, index) => {
    if (seen.has(price.currency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate price for currency "${price.currency}"`,
        path: ['prices', index, 'currency'],
      });
    }
    seen.add(price.currency);
  });
}

/** The three grant-target fields, as they appear on `POST /v1/packages`.
 * `.strict()`-adjacent by construction (this is the only place these three
 * fields are named together) — exactly one of the three must be set, and it
 * must be the one `grantType` names. This is the "defense in depth on top
 * of the DB CHECK" the ADR asks for (§1.2/§9 Slice A): the migration's
 * three-branch CHECK is the actual guarantee, this only gives a client a
 * fast, precise 400 instead of a raw D1 constraint-violation 500. */
const grantTargetShape = {
  grantType: packageGrantTypeSchema,
  examBoardId: idSchema.optional(),
  levelId: idSchema.optional(),
  subjectId: idSchema.optional(),
};

function assertExactlyOneGrantTarget(
  value: { grantType: PackageGrantType; examBoardId?: string; levelId?: string; subjectId?: string },
  ctx: z.RefinementCtx,
) {
  const targets = {
    BOARD: value.examBoardId,
    LEVEL: value.levelId,
    SUBJECT: value.subjectId,
  } as const;
  for (const [tier, id] of Object.entries(targets) as [PackageGrantType, string | undefined][]) {
    const shouldBeSet = tier === value.grantType;
    if (shouldBeSet && !id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `grantType "${value.grantType}" requires ${
          tier === 'BOARD' ? 'examBoardId' : tier === 'LEVEL' ? 'levelId' : 'subjectId'
        }`,
        path: [tier === 'BOARD' ? 'examBoardId' : tier === 'LEVEL' ? 'levelId' : 'subjectId'],
      });
    }
    if (!shouldBeSet && id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `grantType "${value.grantType}" must not set ${
          tier === 'BOARD' ? 'examBoardId' : tier === 'LEVEL' ? 'levelId' : 'subjectId'
        }`,
        path: [tier === 'BOARD' ? 'examBoardId' : tier === 'LEVEL' ? 'levelId' : 'subjectId'],
      });
    }
  }
}

// ─── Multi-grant packages (docs/ADR-multi-grant-packages.md §5) ─────────
//
// A package now grants the UNION of N targets instead of exactly one. The
// per-element exclusivity guarantee that used to apply to the whole body
// (`assertExactlyOneGrantTarget`) now applies PER ARRAY ELEMENT — reused
// verbatim, not duplicated, per the ADR's explicit instruction.
//
// `grantSchema` is the shape of one element of `grants[]`. It intentionally
// mirrors `grantTargetShape` field-for-field so a legacy flat body element
// and a `grants[]` element are structurally identical — the wire format
// diverges only in "one flat object" vs "an array of that object".
export const grantSchema = z.object(grantTargetShape).superRefine(assertExactlyOneGrantTarget);
export type GrantInput = z.infer<typeof grantSchema>;

function grantKey(g: GrantInput): string {
  return `${g.grantType}:${g.examBoardId ?? g.levelId ?? g.subjectId ?? ''}`;
}

/** No two elements of `grants[]` may name the same `(grantType, targetId)`
 * pair — mirrors `assertNoDuplicateCurrency` above, keyed differently.
 * Defense-in-depth on top of `PackageGrant`'s partial unique indexes
 * (ADR §1.1/§5.1) — this gives a precise 400 before D1's constraint 500s. */
export function assertNoDuplicateGrant(grants: GrantInput[], ctx: z.RefinementCtx) {
  const seen = new Set<string>();
  grants.forEach((grant, index) => {
    const key = grantKey(grant);
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate grant (${grant.grantType}) in grants[]`,
        path: ['grants', index],
      });
    }
    seen.add(key);
  });
}

/** Whether the request body used the legacy flat single-target shape
 * (any of `grantType`/`examBoardId`/`levelId`/`subjectId` set directly on
 * the body, as opposed to inside `grants[]`). */
function legacyGrantProvided(value: {
  grantType?: PackageGrantType;
  examBoardId?: string;
  levelId?: string;
  subjectId?: string;
}): boolean {
  return (
    value.grantType !== undefined ||
    value.examBoardId !== undefined ||
    value.levelId !== undefined ||
    value.subjectId !== undefined
  );
}

/** Result of reconciling the legacy flat shape against `grants[]` (ADR
 * §5.2). Deliberately NOT a zod-level rejection for the `conflict` case —
 * `../lib/validate.ts`'s `zValidator` maps EVERY zod issue to the generic
 * `VALIDATION_FAILED` code, but the ADR requires a precise
 * `400 GRANT_SHAPE_CONFLICT` for "caller sent both shapes at once". So this
 * function is called from `PackageService` (business-rule layer, same as
 * `PACKAGE_HAS_SUBSCRIBERS`/`RESOURCE_NOT_VISIBLE`), not from the schema's
 * `superRefine` — it returns a discriminated result instead of throwing, so
 * the caller can pick the right typed `ErrorCode`. */
export type ResolvedGrants =
  | { kind: 'grants'; grants: GrantInput[] }
  | { kind: 'conflict' }
  | { kind: 'none' };

export function resolveGrantsShape(input: {
  grantType?: PackageGrantType;
  examBoardId?: string;
  levelId?: string;
  subjectId?: string;
  grants?: GrantInput[];
}): ResolvedGrants {
  const legacy = legacyGrantProvided(input);
  if (legacy && input.grants) return { kind: 'conflict' };
  if (input.grants) return { kind: 'grants', grants: input.grants };
  if (legacy) {
    return {
      kind: 'grants',
      grants: [
        {
          grantType: input.grantType as PackageGrantType,
          examBoardId: input.examBoardId,
          levelId: input.levelId,
          subjectId: input.subjectId,
        },
      ],
    };
  }
  return { kind: 'none' };
}

// ─── POST /v1/packages ───────────────────────────────────────────────────

export const packageCreateRequestSchema = z
  .object({
    code: codeSchema,
    name: titleSchema,
    description: descriptionSchema,
    // Both shapes are structurally accepted here — legacy fields optional,
    // `grants[]` optional. "Both present" is NOT rejected at this layer (see
    // `resolveGrantsShape`'s doc comment: it needs a specific ErrorCode the
    // generic zValidator failure path can't produce). This schema only
    // enforces: each provided shape is individually well-formed, and at
    // least ONE of the two shapes is present (a package must grant
    // something — ADR §1.2/§5.2).
    ...grantTargetShape,
    grantType: grantTargetShape.grantType.optional(),
    grants: z.array(grantSchema).min(1).max(20).optional(),
    // ADR §1.2 `durationDays Int // CHECK (durationDays > 0)`.
    durationDays: z.number().int().positive('durationDays must be > 0'),
    // Defaults to DRAFT (ADR §1.2/table §1.5) — never offered until an
    // explicit ACTIVE transition.
    status: packageStatusSchema.optional().default(PackageStatus.DRAFT),
    // 0 or more of BDT/USD (ADR §1.3.2 — a BDT-only package is legitimate,
    // there is no fallback/FX conversion for a missing currency).
    prices: z.array(packagePriceSchema).max(8).default([]),
  })
  .superRefine((value, ctx) => {
    assertNoDuplicateCurrency(value.prices, ctx);
    const legacy = legacyGrantProvided(value);
    // Only fully validate the legacy shape when it's the ONLY shape present
    // — when both are present, resolveGrantsShape's GRANT_SHAPE_CONFLICT
    // (service layer) is the authoritative rejection, not a legacy-shape
    // completeness nitpick here.
    if (legacy && !value.grants) {
      if (!value.grantType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'grantType is required when using the legacy grant-target shape',
          path: ['grantType'],
        });
      } else {
        assertExactlyOneGrantTarget(
          value as {
            grantType: PackageGrantType;
            examBoardId?: string;
            levelId?: string;
            subjectId?: string;
          },
          ctx,
        );
      }
    }
    if (value.grants) {
      assertNoDuplicateGrant(value.grants, ctx);
    }
    if (!legacy && !value.grants) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'A package must grant something: provide either the legacy grantType/*Id fields or grants[].',
        path: ['grants'],
      });
    }
  });
export type PackageCreateRequest = z.infer<typeof packageCreateRequestSchema>;

// ─── PATCH /v1/packages/:id ──────────────────────────────────────────────
// `prices`, when present, REPLACES the full price set (same "explicit full
// replace" shape as `./level.ts`'s `levelStructureRequestSchema`) — never a
// partial per-currency patch, so "two conflicting BDT prices" can't arise
// from a partial merge either.
//
// The four grant-target fields are atomic: editing the grant target means
// supplying ALL of `grantType` + the one matching id together (never just
// one bare field) — this DTO cannot express "change only examBoardId",
// which is deliberate: a lone `examBoardId` with no `grantType` would be
// ambiguous about which tier it's meant to (re)target.
//
// `grants[]` (docs/ADR-multi-grant-packages.md §5.2), when present, is a
// FULL REPLACE of the package's whole grant set — same convention as
// `prices` above, never a partial per-grant patch. `grants[]` and the
// legacy flat shape are mutually exclusive on the wire; sending both is
// `400 GRANT_SHAPE_CONFLICT`, resolved in `../services/packages.ts` (see
// `resolveGrantsShape`'s doc comment above for why that specific check
// lives there and not in this schema). Neither present on a PATCH is NOT a
// grant edit at all — other fields patch normally, matching today's
// behaviour. `PackageService.update` diffs the requested grant SET against
// what's currently in the DB (never the request body) to decide whether to
// bump the access epoch (ADR §4) — this DTO has no opinion on that.
//
// ⚠ Per PO ruling (S3 brief, 2026-09-02): a grant-set edit on a package with
// live `StudentPackage` subscribers is ALLOWED (no 409 block), gated only
// by a UI warning (frontend, separate task) showing the live subscriber
// count. This supersedes ADR §5.4's "keep the blanket 409" recommendation.

export const packageUpdateRequestSchema = z
  .object({
    code: codeSchema.optional(),
    name: titleSchema.optional(),
    description: descriptionSchema,
    durationDays: z.number().int().positive('durationDays must be > 0').optional(),
    status: packageStatusSchema.optional(),
    prices: z.array(packagePriceSchema).max(8).optional(),
    grants: z.array(grantSchema).min(1).max(20).optional(),
  })
  .merge(z.object(grantTargetShape).partial())
  .superRefine((value, ctx) => {
    const touchesTarget = legacyGrantProvided(value);
    // Same "only fully validate the legacy shape when it's the ONLY shape
    // present" reasoning as the create schema above.
    if (touchesTarget && !value.grants) {
      if (!value.grantType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Editing the grant target requires grantType plus its matching id',
          path: ['grantType'],
        });
      } else {
        assertExactlyOneGrantTarget(
          value as { grantType: PackageGrantType; examBoardId?: string; levelId?: string; subjectId?: string },
          ctx,
        );
      }
    }
    if (value.grants) assertNoDuplicateGrant(value.grants, ctx);
    if (value.prices) assertNoDuplicateCurrency(value.prices, ctx);
  });
export type PackageUpdateRequest = z.infer<typeof packageUpdateRequestSchema>;

// ─── GET /v1/packages ────────────────────────────────────────────────────

export const packageListQuerySchema = z.object({
  includeArchived: z
    .union([z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === 'true'),
});
export type PackageListQuery = z.infer<typeof packageListQuerySchema>;

// ─── POST /v1/students/:id/packages ──────────────────────────────────────
// Manual assignment (ADR §6.1/§6.4) — `startsAt` is NEVER accepted from the
// client (server-derived: `now`, or the ADR §6.5 D3 append rule when the
// student already holds a live row for the same package) so a caller can't
// backdate/forward-date a grant.

export const studentPackageAssignRequestSchema = z.object({
  packageId: idSchema,
});
export type StudentPackageAssignRequest = z.infer<typeof studentPackageAssignRequestSchema>;
